import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TopExperienceLayer from '../components/top/TopExperienceLayer';
import Footer from '../components/layout/Footer';
import {
  BookOpen, Download, Heart, Users, Calendar, GraduationCap,
  CalendarDays, Gift, Bookmark, Building2, Play, Lock, ArrowRight, ChevronRight
} from 'lucide-react';

const PRIMARY   = '#1a5d3a';
const GOLD      = '#f5a623';
const _GOLD_HOV  = '#e09000';
const CREAM     = '#f5f0e8';
const SAGE      = '#e8f5e9';
const WHITE     = '#FFFFFF';
const WARM_CARD = '#fff8e7';

const MAGAZINE_ISSUES = [
  { number: 7,  season: 'Fall 2024',   headline: 'Level Up Your Senior Year',   from: '#d4e6da', to: '#b8d6c2', accent: PRIMARY },
  { number: 8,  season: 'Winter 2024', headline: 'Make Winter Count',            from: '#e8f5e9', to: '#c8e6c9', accent: PRIMARY },
  { number: 9,  season: 'Spring 2024', headline: 'Spring Into Opportunity',      from: '#fff8e7', to: '#fdebc8', accent: GOLD },
  { number: 10, season: 'Summer 2024', headline: 'Summer Built Different',       from: '#e8f4f0', to: '#c8e8dc', accent: PRIMARY },
  { number: 11, season: 'Winter 2025', headline: 'New Year New You',             from: '#f5f0e8', to: '#e8e0d0', accent: PRIMARY },
  { number: 12, season: 'Spring 2025', headline: 'Your Future Awaits',           from: '#1a5d3a', to: '#2d7a4f', accent: GOLD, current: true },
];

const PILLARS = [
  { icon: CalendarDays, label: 'Live Events',           desc: 'Join exclusive Q&As, panels, and expert sessions.' },
  { icon: Gift,         label: 'Spin & Win',            desc: 'Spin weekly for prizes, swag, and surprises.' },
  { icon: Bookmark,     label: 'Scholarship Spotlights',desc: 'Find legit scholarship opportunities every month.' },
  { icon: Building2,    label: 'Campus Stories',        desc: 'Real student stories from real college campuses.' },
];

export default function MagazinePage() {
  const [email, _setEmail] = useState('');
  const [_joined, setJoined] = useState(false);

  const _handleJoin = (e) => {
    e.preventDefault();
    if (email) setJoined(true);
  };

  return (
    <div style={{ background: WHITE, minHeight: '100vh' }}>
      <TopExperienceLayer />

      {/* ── HERO ── */}
      <section style={{ background: PRIMARY, padding: '72px 0 64px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              {/* Logo mark */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 flex items-center justify-center font-black text-white text-lg"
                  style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.3)' }}
                >S</div>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-white/80">StudentSignal</div>
                  <div className="text-xs tracking-wider uppercase text-white/50">Magazine</div>
                </div>
              </div>

              <h1 className="font-black leading-none mb-2 text-white"
                style={{ fontSize: 'clamp(2.8rem, 6vw, 4.5rem)', letterSpacing: '-0.02em' }}>
                STUDENT<span style={{ color: GOLD }}>SIGNAL</span>
              </h1>
              <h2 className="font-black leading-none mb-4 text-white"
                style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '0.1em' }}>
                MAGAZINE
              </h2>
              <div className="w-16 h-1 mb-4 rounded" style={{ background: GOLD }} />
              <p className="font-semibold mb-2 text-white/90" style={{ fontSize: '1.25rem' }}>
                Discover. Prepare. Succeed.
              </p>
              <p className="mb-8 text-white/70" style={{ fontSize: '1rem', lineHeight: 1.7 }}>
                The monthly digital magazine for <span style={{ color: GOLD }}>ambitious</span> high school students.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 mb-8">
                <button
                  className="flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all hover:opacity-90"
                  style={{ background: GOLD, color: WHITE, borderRadius: '6px' }}
                >
                  <BookOpen size={18} />
                  Read Online Free
                </button>
                <button
                  className="flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all"
                  style={{ border: '2px solid rgba(255,255,255,0.5)', color: WHITE, borderRadius: '6px' }}
                >
                  <Download size={18} />
                  Download Issue
                </button>
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap gap-5">
                {[
                  { icon: Heart,         text: '100% FREE · For Students' },
                  { icon: Users,         text: 'Trusted by 100K+ Readers' },
                  { icon: Calendar,      text: 'New Issue Every Month' },
                  { icon: GraduationCap, text: 'Built for High School' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-1.5">
                    <Icon size={14} style={{ color: GOLD }} />
                    <span className="text-xs text-white/70">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: stacked magazine covers */}
            <div className="hidden lg:flex items-center justify-center relative h-[380px]">
              <div
                className="absolute"
                style={{
                  width: 200, height: 270, borderRadius: '6px', overflow: 'hidden',
                  transform: 'rotate(8deg) translateX(60px)',
                  background: `linear-gradient(160deg, ${SAGE} 0%, #c8e6c9 100%)`,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <div className="p-4">
                  <div className="text-xs font-bold tracking-widest mb-3" style={{ color: PRIMARY }}>STUDENTSIGNAL</div>
                  <div className="text-lg font-black leading-tight" style={{ color: PRIMARY }}>SUMMER BUILT DIFFERENT</div>
                  <div className="text-xs mt-2" style={{ color: PRIMARY + '99' }}>ISSUE 10 · SUMMER 2024</div>
                </div>
                <div className="absolute bottom-0 w-full" style={{ height: 80, background: `linear-gradient(to top, ${PRIMARY}22, transparent)` }} />
              </div>

              <div
                className="absolute"
                style={{
                  width: 200, height: 270, borderRadius: '6px', overflow: 'hidden',
                  transform: 'rotate(-3deg) translateX(-30px)',
                  background: `linear-gradient(160deg, #fff8e7 0%, #fdebc8 100%)`,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <div className="p-4">
                  <div className="text-xs font-bold tracking-widest mb-3" style={{ color: GOLD }}>STUDENTSIGNAL</div>
                  <div className="text-xl font-black leading-tight" style={{ color: '#1a1a1a' }}>NEW YEAR NEW YOU</div>
                  <div className="text-xs mt-2" style={{ color: '#888' }}>ISSUE 11 · WINTER 2025</div>
                  <div className="mt-2 inline-block px-2 py-0.5 text-xs font-bold rounded" style={{ background: GOLD, color: WHITE }}>NEW</div>
                </div>
              </div>

              <div
                className="absolute z-10"
                style={{
                  width: 210, height: 285, borderRadius: '6px', overflow: 'hidden',
                  transform: 'rotate(-1deg) translateX(20px)',
                  background: `linear-gradient(160deg, ${PRIMARY} 0%, #2d7a4f 100%)`,
                  boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <div className="p-4">
                  <div className="text-xs font-bold tracking-widest mb-3" style={{ color: GOLD }}>STUDENTSIGNAL MAGAZINE</div>
                  <div className="text-2xl font-black text-white leading-tight mb-1">YOUR FUTURE AWAITS</div>
                  <div className="text-xs mt-2 text-white/60">ISSUE 12 · SPRING 2025</div>
                  <div className="mt-3 text-xs font-bold text-white/80">EXCLUSIVE</div>
                  <div className="text-xs font-black" style={{ color: GOLD }}>HARVARD ADMISSIONS<br />DIRECTOR Q&A</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LIVE EVENT BANNER ── */}
      <div
        className="w-full flex items-center justify-center gap-3 py-3 cursor-pointer hover:opacity-90 transition-opacity"
        style={{ background: '#C62828' }}
      >
        <span className="text-sm font-bold text-white flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse inline-block" />
          LIVE July 15 — Harvard Admissions Director Q&A — inside this issue
        </span>
        <ChevronRight size={16} className="text-white/80" />
      </div>

      {/* ── FEATURE PILLARS ── */}
      <section className="py-16" style={{ background: WHITE }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {PILLARS.map(({ icon: Icon, label, desc }) => (
              <div key={label}>
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: SAGE }}>
                    <Icon size={26} style={{ color: PRIMARY }} />
                  </div>
                </div>
                <div className="font-bold text-sm tracking-wide mb-2" style={{ color: '#111' }}>{label}</div>
                <p className="text-xs leading-relaxed" style={{ color: '#666' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PAST ISSUES GRID ── */}
      <section className="py-16" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl font-bold" style={{ color: '#111' }}>Explore Past Issues</h2>
            <button className="flex items-center gap-1 text-sm font-semibold" style={{ color: PRIMARY }}>
              View All Issues <ArrowRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {MAGAZINE_ISSUES.map((issue) => (
              <div key={issue.number}>
                <div
                  className="relative overflow-hidden mb-3"
                  style={{
                    background: `linear-gradient(160deg, ${issue.from} 0%, ${issue.to} 100%)`,
                    borderRadius: '6px',
                    paddingTop: '135%',
                    boxShadow: issue.current
                      ? `0 4px 20px rgba(26,93,58,0.3), 0 0 0 2px ${PRIMARY}`
                      : '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <div className="absolute inset-0 p-3">
                    <div className="text-[9px] font-bold tracking-widest mb-1"
                      style={{ color: issue.current ? 'rgba(255,255,255,0.7)' : PRIMARY + '99' }}>
                      STUDENTSIGNAL
                    </div>
                    <div className="font-black leading-tight"
                      style={{ fontSize: '0.7rem', color: issue.current ? WHITE : '#1a1a1a' }}>
                      {issue.headline}
                    </div>
                    <div className="text-[8px] mt-1"
                      style={{ color: issue.current ? 'rgba(255,255,255,0.5)' : '#888' }}>
                      ISSUE {issue.number}
                    </div>
                    {issue.current && (
                      <div className="absolute top-2 right-2 px-1.5 py-0.5 text-[8px] font-black rounded"
                        style={{ background: GOLD, color: WHITE }}>NEW</div>
                    )}
                  </div>
                </div>
                <div className="text-xs font-bold mb-0.5" style={{ color: '#111' }}>Issue {String(issue.number).padStart(2, '0')}</div>
                <div className="text-xs mb-2" style={{ color: '#888' }}>{issue.season}</div>
                <button
                  className="w-full py-1.5 text-xs font-semibold transition-all hover:opacity-90"
                  style={{
                    border:      issue.current ? 'none' : `1px solid #ccc`,
                    background:  issue.current ? PRIMARY : 'transparent',
                    color:       issue.current ? WHITE : '#333',
                    borderRadius: '4px',
                  }}
                >
                  Read Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLUR GATE ── */}
      <section className="py-16" style={{ background: WHITE }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-xl overflow-hidden" style={{ border: '1px solid #e0e0e0' }}>
            <div className="grid grid-cols-3" style={{ filter: 'blur(6px)', userSelect: 'none', opacity: 0.4 }}>
              {['TOP 10 COLLEGE APPLICATION TIPS', 'SCHOLARSHIP SECRETS', 'FINANCIAL AID MYTHS — BUSTED'].map((title) => (
                <div key={title} className="bg-white p-6 h-40">
                  <div className="font-black text-lg mb-2" style={{ color: '#1a1a1a' }}>{title}</div>
                  <div className="text-xs text-gray-500 leading-relaxed">
                    Lorem ipsum dolor sit amet consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore.
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm px-4 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: PRIMARY }}>
                <Lock size={22} className="text-white" />
              </div>
              <h3 className="text-xl font-black mb-2" style={{ color: '#111' }}>Join Free to Read</h3>
              <p className="text-sm mb-5" style={{ color: '#555', maxWidth: 320 }}>
                Unlock full issue access, bonus pages, and exclusive student opportunities.
              </p>
              <button
                className="flex items-center gap-2 px-7 py-3 font-bold text-sm text-white transition-all hover:opacity-90"
                style={{ background: GOLD, borderRadius: '6px' }}
              >
                Join Free Now <ChevronRight size={16} />
              </button>
              <p className="text-xs mt-3" style={{ color: '#888' }}>No credit card. Always free for students.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STUDENT DOOR CROSSLINK ── */}
      <section className="py-12" style={{ background: CREAM }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center gap-6"
            style={{ background: WARM_CARD, border: '1px solid #e8d8b0' }}
          >
            <div
              className="w-20 h-20 flex-shrink-0 flex items-center justify-center rounded-xl"
              style={{ background: SAGE, border: `1px solid #c8e6c9` }}
            >
              <span className="font-black text-3xl" style={{ color: PRIMARY }}>SD</span>
            </div>
            <div className="flex-1">
              <div className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: PRIMARY }}>PODCAST</div>
              <h3 className="text-lg font-bold mb-1" style={{ color: '#1a1a1a' }}>This month on Student Door</h3>
              <div className="text-sm mb-1" style={{ color: '#888' }}>Episode 28 · July 2025</div>
              <div className="font-bold mb-2" style={{ color: '#1a1a1a' }}>From Rejection to Acceptance: Lessons No One Tells You</div>
              <p className="text-sm" style={{ color: '#666' }}>
                A candid conversation with students who turned setbacks into comebacks — and how you can too.
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-4">
              <Link to="/podcast">
                <button
                  className="flex items-center gap-2 px-5 py-2.5 font-bold text-sm text-white transition-all hover:opacity-90"
                  style={{ background: PRIMARY, borderRadius: '6px' }}
                >
                  <Play size={16} fill="white" />
                  Listen Now
                </button>
              </Link>
              <span className="text-sm" style={{ color: '#888' }}>34 min</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── CROSS-LINKS SECTION ── */}
      <section className="py-12" style={{ background: WHITE, borderTop: '1px solid #eee' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-xs font-bold tracking-widest uppercase mb-8" style={{ color: '#999' }}>
            MORE FROM STUDENTSIGNAL
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'OK NEWSLETTER', sub: 'Weekly opportunities for students.', to: '/newsletter', cta: 'Subscribe Free' },
              { title: 'STUDENT DOOR', sub: 'The twice-monthly podcast that gives you more.', to: '/podcast', cta: 'Listen Now' },
              { title: 'STUDENTSIGNAL', sub: 'Find colleges and scholarships built for you.', to: '/', cta: 'Get Started' },
            ].map(({ title, sub, to, cta }) => (
              <div key={title} className="rounded-xl p-6 text-center" style={{ background: SAGE, border: '1px solid #c8e6c9' }}>
                <div className="font-black mb-2" style={{ color: PRIMARY }}>{title}</div>
                <p className="text-sm mb-4" style={{ color: '#555' }}>{sub}</p>
                <Link to={to}>
                  <button className="px-5 py-2 text-sm font-bold text-white rounded transition-all hover:opacity-90"
                    style={{ background: PRIMARY }}>
                    {cta}
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
